import { useEffect, useState } from "react";
import { CalendarDays, Clock, MapPin, Users, Lock, Loader2 } from "lucide-react";
import { API } from "../../lib/api";
import { fmtTime, fmtLong, fmtShort, weekdayOf, programColor, isFull, isBookingClosed } from "../../lib/calendarUtils";

const fmtMoney = (n) => (typeof n === "number" ? `$${n.toFixed(2)}` : "—");

export default function ClassDetailsPanel({ occ, onBook, compact = false }) {
  const s = occ?.schedule;
  const options = s?.payment_options?.length ? s.payment_options : null;
  const [optId, setOptId] = useState(null);
  const [dates, setDates] = useState({ loading: false, items: null });
  const [sessionDate, setSessionDate] = useState(null);

  // Reset selection when the selected class changes
  useEffect(() => {
    setOptId(options ? options[0].option_id : null);
    setSessionDate(null);
  }, [occ?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const opt = options ? options.find((o) => o.option_id === optId) || options[0] : null;
  const requiresDate = !!opt?.requires_session_date;

  // Load per-date availability for the selected schedule (dates for drop-in/trial,
  // and the CRM-accurate first bookable date for the monthly start note)
  useEffect(() => {
    if (!s?.id) return;
    let cancelled = false;
    setDates({ loading: true, items: null });
    fetch(`${API}/site/schedule-occurrences/${s.id}`)
      .then((r) => { if (!r.ok) throw new Error("bad"); return r.json(); })
      .then((d) => {
        if (cancelled) return;
        const items = d.occurrences || [];
        setDates({ loading: false, items });
        const preferred = items.find((o) => o.date === occ?.date && o.bookable) || items.find((o) => o.bookable);
        setSessionDate(preferred?.date || null);
      })
      .catch(() => { if (!cancelled) setDates({ loading: false, items: [] }); });
    return () => { cancelled = true; };
  }, [s?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!occ) {
    return (
      <div className="rounded-[1.5rem] border border-slate-200 bg-white p-8 text-center" data-testid="details-panel-empty">
        <p className="font-display font-bold text-slate-700">Select a class on the calendar</p>
        <p className="mt-1.5 text-xs text-slate-500">Tap any time slot to see details, pricing and enrollment.</p>
      </div>
    );
  }

  const c = programColor(s);
  const p = opt ? opt.pricing || {} : s.pricing || {};
  const recurring = opt ? !!opt.recurring : s.billing_type === "subscription";
  const isTrial = opt?.type === "trial";
  const full = isFull(s);
  const closed = isBookingClosed(s);
  const days = (s.weekdays || []).join(" · ");
  const noDatesAvailable = requiresDate && dates.items && !dates.items.some((d) => d.bookable);
  const selectedOccFull = requiresDate && sessionDate && dates.items
    ? !(dates.items.find((d) => d.date === sessionDate)?.bookable)
    : false;
  const needsDatePick = requiresDate && !sessionDate;
  const monthlyStart = recurring
    ? (dates.items?.find((d) => d.bookable)?.date || dates.items?.[0]?.date || null)
    : null;

  let btnLabel = recurring ? "Enroll Monthly" : requiresDate ? (isTrial ? "Book Trial" : "Book Drop-in") : "Book Now";
  if (closed) btnLabel = "Booking closed";
  else if (full) btnLabel = "Full";
  else if (noDatesAvailable) btnLabel = "No dates available";
  else if (selectedOccFull) btnLabel = "Date full — pick another";
  else if (needsDatePick) btnLabel = "Select a date";
  const disabled = full || closed || needsDatePick || selectedOccFull || noDatesAvailable;

  return (
    <div
      className={`rounded-[1.5rem] border border-slate-200 bg-white overflow-hidden ${compact ? "" : "shadow-sm"}`}
      data-testid="class-details-panel"
    >
      <div className="h-1.5" style={{ backgroundColor: c.hex }} />
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="pill text-[10px] font-bold" style={{ backgroundColor: `${c.hex}1A`, color: c.hex }}>
            {(s.schedule_type || "").replace(/_/g, " ")}
          </span>
          {s.age_range && <span className="pill bg-slate-100 text-slate-600 text-[10px] font-bold">Ages {s.age_range}</span>}
        </div>
        <h2 className="mt-2.5 font-display text-xl font-extrabold tracking-tight leading-snug" style={{ color: "#123B6D" }} data-testid="details-title">
          {s.title}
        </h2>
        {s.description && <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-4">{s.description}</p>}

        <div className="mt-4 grid gap-2 text-sm text-slate-700">
          <span className="inline-flex items-center gap-2" data-testid="details-date">
            <CalendarDays className="h-4 w-4 shrink-0 text-[#00A6B2]" /> {fmtLong(occ.date)}
          </span>
          <span className="inline-flex items-center gap-2">
            <Clock className="h-4 w-4 shrink-0 text-[#00A6B2]" />
            {days ? `${days} · ` : ""}
            {fmtTime(s.start_time)}–{fmtTime(s.end_time)}
          </span>
          {s.location && (
            <span className="inline-flex items-center gap-2">
              <MapPin className="h-4 w-4 shrink-0 text-[#00A6B2]" /> {s.location}
            </span>
          )}
          <span className="inline-flex items-center gap-2" data-testid="details-seats">
            <Users className="h-4 w-4 shrink-0 text-[#00A6B2]" />
            {closed ? "Online booking closed" : full ? "No seats available" : `${s.seats_available} seat${s.seats_available === 1 ? "" : "s"} available`}
          </span>
        </div>

        {/* Payment option selector (CRM payment_options) */}
        {options && options.length > 1 && (
          <div className="mt-5 grid gap-2" role="radiogroup" aria-label="Payment options" data-testid="payment-options">
            {options.map((o) => {
              const op = o.pricing || {};
              const sel = o.option_id === (opt?.option_id || "");
              return (
                <button
                  key={o.option_id}
                  type="button"
                  role="radio"
                  aria-checked={sel}
                  onClick={() => setOptId(o.option_id)}
                  data-testid={`payment-option-${o.option_id}`}
                  className={`flex items-center justify-between gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-colors ${
                    sel ? "border-[#123B6D] bg-[#EAF7FB]/60" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <span className="flex items-center gap-2.5 min-w-0">
                    <span className={`h-4 w-4 shrink-0 rounded-full border-2 ${sel ? "border-[#123B6D]" : "border-slate-300"} flex items-center justify-center`}>
                      {sel && <span className="h-2 w-2 rounded-full bg-[#123B6D]" />}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-bold text-slate-900 truncate">{o.label}</span>
                      {o.public_description && <span className="block text-xs text-slate-500 truncate">{o.public_description}</span>}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-extrabold" style={{ color: "#123B6D" }}>
                    {o.recurring ? `${fmtMoney(op.monthly_amount)}/mo` : `${fmtMoney(op.total_due_today)}`}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Drop-in date picker */}
        {requiresDate && !closed && !full && (
          <div className="mt-4" data-testid="dropin-date-picker">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Choose a class date</p>
            {dates.loading ? (
              <p className="mt-2 inline-flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading dates…</p>
            ) : (dates.items || []).length === 0 ? (
              <p className="mt-2 text-xs text-slate-500" data-testid="dropin-no-dates">
                No upcoming dates are available for this option right now. Other booking options may still be open.
              </p>
            ) : (
              <div className="mt-2 grid grid-cols-3 gap-1.5 max-h-44 overflow-y-auto pr-1">
                {dates.items.map((d) => {
                  const sel = d.date === sessionDate;
                  return (
                    <button
                      key={d.date}
                      type="button"
                      disabled={!d.bookable}
                      onClick={() => setSessionDate(d.date)}
                      data-testid={`dropin-date-${d.date}`}
                      className={`rounded-xl border px-2 py-2 text-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        sel ? "border-[#123B6D] bg-[#123B6D] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      <span className="block text-[10px] font-bold uppercase opacity-75">{weekdayOf(d.date)}</span>
                      <span className="block text-xs font-extrabold">{fmtShort(d.date)}</span>
                      <span className={`block text-[9px] font-semibold ${sel ? "text-white/80" : d.bookable ? "text-emerald-600" : "text-slate-400"}`}>
                        {d.bookable ? `${d.seats_available} seats` : (d.seats_available ?? 0) > 0 ? "Closed" : "Full"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {selectedOccFull && (
              <p className="mt-2 text-xs font-semibold text-amber-700" data-testid="dropin-date-full-msg">
                That date is fully booked — please choose another available date above.
              </p>
            )}
          </div>
        )}

        <dl className="mt-5 grid gap-1 text-sm rounded-2xl bg-slate-50 border border-slate-100 p-4" data-testid="details-pricing">
          <div className="flex justify-between text-slate-600"><dt>Tuition</dt><dd>{fmtMoney(p.base_price)}</dd></div>
          <div className="flex justify-between text-slate-600"><dt>Online Booking Fee</dt><dd>{fmtMoney(p.booking_fee_amount)}</dd></div>
          <div className="flex justify-between text-slate-600"><dt>GST</dt><dd>{fmtMoney(p.tax_amount)}</dd></div>
          <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-1.5 mt-1" data-testid="details-total">
            <dt>Total due today</dt><dd>{fmtMoney(p.total_due_today)}</dd>
          </div>
          {recurring && (
            <p className="text-xs text-slate-500 pt-1" data-testid="details-monthly-label">
              {opt?.recurring_note
                ? `${fmtMoney(p.monthly_amount)} per month — ${opt.recurring_note}`
                : `${fmtMoney(p.monthly_amount)} per month — billed monthly until cancelled.`}
            </p>
          )}
          {recurring && monthlyStart && (
            <p className="text-xs text-slate-500" data-testid="details-monthly-start">
              First class: {fmtLong(monthlyStart)} — enrollment continues weekly{days ? ` on ${days}` : ""} at {fmtTime(s.start_time)}.
            </p>
          )}
          {!recurring && requiresDate && sessionDate && (
            <p className="text-xs text-slate-500 pt-1" data-testid="details-dropin-label">
              One-time {isTrial ? "trial class" : ""} payment for {fmtLong(sessionDate)}.
            </p>
          )}
          {isTrial && (
            <p className="text-xs text-slate-500 pt-1" data-testid="details-trial-note">
              Trial classes are limited per student — eligibility is confirmed automatically when you book.
            </p>
          )}
          {opt?.cancellation && (
            <p className="text-xs text-slate-500 pt-1" data-testid="details-cancellation">{opt.cancellation}</p>
          )}
        </dl>

        <button
          type="button"
          disabled={disabled}
          onClick={() => onBook({ schedule: s, option: opt, sessionDate: requiresDate ? sessionDate : null })}
          data-testid="details-book-btn"
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ backgroundColor: disabled ? "#94A3B8" : "#FF8C42" }}
        >
          {(full || closed) && <Lock className="h-3.5 w-3.5" />}
          {btnLabel}
        </button>
      </div>
    </div>
  );
}
